import type {
  HsFormatDecodeContext,
  HsFormatDecoder,
  HsFrustumDataView,
  HsFrustumView,
  HsGeometryComponentView,
  HsViewerPlugin,
  HsViewerPluginContext,
  HsDisposable,
} from '@sibe/hyperscene';

type BinaryFrustumItem = HsGeometryComponentView & { worldMatrix: number[] };

export const HS_FRUSTUM_V4_CONTENT_TYPE: string = 'application/vnd.hyperscene.frustum.v4';
export const HS_FRUSTUM_JSON_CONTENT_TYPE: string = 'application/json';
const MATRIX_GROUPS: readonly (readonly number[])[] = [
  [0, 1, 2, 4, 5, 6, 8, 9, 10],
  [3],
  [7],
  [11],
  [12],
  [13],
  [14],
  [15],
];

/** One object of the compact JSON Frustum response; numbers index `ids` and `titles` (-1 means none). */
export interface HsCompactFrustumItem {
  node: number;
  tree: number;
  geometry: number;
  originalMaterial: number;
  sceneMaterial: number;
  textureId: number;
  title: number;
  matrix: number[];
}

export interface HsCompactFrustumData {
  type: string;
  toAdd: HsCompactFrustumItem[];
  toDelete: number[];
}

/** Compact JSON Frustum response (`Accept: application/json` on `FrustumCompact`). */
export interface HsCompactFrustumView {
  ids: string[];
  titles: string[];
  frustumData: HsCompactFrustumData[];
}

export function hsDecodeJsonFrustum(view: HsCompactFrustumView): HsFrustumView {
  const resolveId = (index: number, optional: boolean): string | null => {
    if (optional && index === -1) {
      return null;
    }
    if (!Number.isInteger(index) || index < 0 || index >= view.ids.length) {
      throw new Error('Invalid JSON Frustum response: ID index.');
    }
    return view.ids[index];
  };
  const resolveTitle = (index: number): string | undefined => {
    if (index === -1) {
      return undefined;
    }
    if (!Number.isInteger(index) || index < 0 || index >= view.titles.length) {
      throw new Error('Invalid JSON Frustum response: title index.');
    }
    return view.titles[index];
  };
  if (!Array.isArray(view.ids) || !Array.isArray(view.titles) || !Array.isArray(view.frustumData)) {
    throw new Error('Invalid JSON Frustum response: payload.');
  }
  return {
    frustumData: view.frustumData.map((data: HsCompactFrustumData): HsFrustumDataView => {
      if (!Array.isArray(data.toAdd) || !Array.isArray(data.toDelete)) {
        throw new Error('Invalid JSON Frustum response: data.');
      }
      return {
        type: data.type,
        toDelete: data.toDelete.map((index: number): string => {
          const id: string | null = resolveId(index, false);
          if (id === null) {
            throw new Error('Invalid JSON Frustum response: required ID.');
          }
          return id;
        }),
        toAdd: data.toAdd.map((item: HsCompactFrustumItem): BinaryFrustumItem => {
          if (!Array.isArray(item.matrix) || item.matrix.length !== 16 || !item.matrix.every(Number.isFinite)) {
            throw new Error('Invalid JSON Frustum response: matrix.');
          }
          const nodeId: string | null = resolveId(item.node, false);
          const treeId: string | null = resolveId(item.tree, false);
          if (nodeId === null || treeId === null) {
            throw new Error('Invalid JSON Frustum response: required ID.');
          }
          return {
            nodeId,
            treeId,
            geometryId: resolveId(item.geometry, true),
            originalMaterialId: resolveId(item.originalMaterial, true),
            sceneMaterialId: resolveId(item.sceneMaterial, true),
            textureId: resolveId(item.textureId, true),
            nodeTitle: resolveTitle(item.title),
            worldMatrix: item.matrix,
          };
        }),
      };
    }),
  };
}

export function hsDecodeBinaryFrustum(buffer: ArrayBuffer): HsFrustumView {
  const bytes: Uint8Array = new Uint8Array(buffer);
  const data: DataView = new DataView(buffer);
  const utf8: TextDecoder = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true });
  let offset: number = 0;
  const requireBytes = (count: number): void => {
    if (count > bytes.length - offset) {
      throw new Error('Invalid binary Frustum response: truncated data.');
    }
  };
  const readInt = (): number => {
    requireBytes(4);
    const value: number = data.getInt32(offset, true);
    offset += 4;
    return value;
  };
  const readCount = (): number => {
    const value: number = readInt();
    if (value < 0) {
      throw new Error('Invalid binary Frustum response: negative count.');
    }
    return value;
  };
  const readString = (): string => {
    const count: number = readCount();
    requireBytes(count);
    const value: string = utf8.decode(bytes.subarray(offset, offset + count));
    offset += count;
    return value;
  };
  requireBytes(20);
  if (bytes[0] !== 72 || bytes[1] !== 83 || bytes[2] !== 70 || bytes[3] !== 67) {
    throw new Error('Invalid binary Frustum response: signature.');
  }
  offset = 4;
  const version: number = readInt();
  if (version !== 4) {
    throw new Error('Unsupported binary Frustum response version.');
  }
  const idCount: number = readCount();
  const titleCount: number = readCount();
  const typeCount: number = readCount();
  requireBytes(idCount * 16 + titleCount * 4 + typeCount * 12);
  const ids: string[] = new Array<string>(idCount);
  const hex: string[] = Array.from({ length: 256 }, (_: unknown, value: number) => value.toString(16).padStart(2, '0'));
  const parts: string[] = new Array<string>(20);
  const positions: readonly number[] = [0, 1, 2, 3, 5, 6, 8, 9, 11, 12, 14, 15, 16, 17, 18, 19];
  parts[4] = parts[7] = parts[10] = parts[13] = '-';
  for (let index: number = 0; index < idCount; index++) {
    for (let byte: number = 0; byte < 16; byte++) {
      parts[positions[byte]] = hex[bytes[offset++]];
    }
    ids[index] = parts.join('');
  }
  const titles: string[] = Array.from({ length: titleCount }, readString);
  const resolveId = (index: number, optional: boolean): string | null => {
    if (optional && index === -1) {
      return null;
    }
    if (index < 0 || index >= ids.length) {
      throw new Error('Invalid binary Frustum response: ID index.');
    }
    return ids[index];
  };
  const readRequiredId = (): string => {
    const id: string | null = resolveId(readInt(), false);
    if (id === null) {
      throw new Error('Invalid binary Frustum response: required ID.');
    }
    return id;
  };
  const readDictionaryMatrices = (toAdd: BinaryFrustumItem[]): void => {
    const count: number = toAdd.length;
    for (const columns of MATRIX_GROUPS) {
      const entries: number = readCount();
      if (entries > count) {
        throw new Error('Invalid binary Frustum response: matrix dictionary count.');
      }
      if (entries === 0) {
        requireBytes(count * columns.length * 8);
        for (const column of columns) {
          for (const item of toAdd) {
            const value: number = data.getFloat64(offset, true);
            offset += 8;
            if (!Number.isFinite(value)) {
              throw new Error('Invalid binary Frustum response: non-finite matrix.');
            }
            item.worldMatrix[column] = value;
          }
        }
        continue;
      }
      const length: number = entries;
      const width: number = entries <= 1 ? 0 : entries <= 65536 ? 2 : 4;
      requireBytes(length * columns.length * 8 + count * width);
      const values: Float64Array = new Float64Array(length * columns.length);
      for (let index: number = 0; index < values.length; index++) {
        const value: number = data.getFloat64(offset, true);
        offset += 8;
        if (!Number.isFinite(value)) {
          throw new Error('Invalid binary Frustum response: non-finite matrix.');
        }
        values[index] = value;
      }
      for (let row: number = 0; row < count; row++) {
        const index: number =
          width === 0 ? 0 : width === 2 ? data.getUint16(offset, true) : data.getUint32(offset, true);
        offset += width;
        if (index >= entries) {
          throw new Error('Invalid binary Frustum response: matrix dictionary index.');
        }
        for (let column: number = 0; column < columns.length; column++) {
          toAdd[row].worldMatrix[columns[column]] = values[column * length + index];
        }
      }
    }
  };
  const frustumData: HsFrustumDataView[] = [];
  for (let typeIndex: number = 0; typeIndex < typeCount; typeIndex++) {
    const type: string = readString();
    const count: number = readCount();
    const deleteCount: number = readCount();
    requireBytes(deleteCount * 4 + count * 28 + 32);
    const toDelete: string[] = Array.from({ length: deleteCount }, readRequiredId);
    const toAdd: BinaryFrustumItem[] = Array.from({ length: count }, () => ({
      nodeId: readRequiredId(),
      treeId: '',
      nodeTitle: undefined,
      geometryId: null,
      originalMaterialId: null,
      sceneMaterialId: null,
      worldMatrix: new Array<number>(16),
    }));
    for (const item of toAdd) {
      item.treeId = readRequiredId();
    }
    for (const item of toAdd) {
      item.geometryId = resolveId(readInt(), true);
    }
    for (const item of toAdd) {
      item.originalMaterialId = resolveId(readInt(), true);
    }
    for (const item of toAdd) {
      item.sceneMaterialId = resolveId(readInt(), true);
    }
    for (const item of toAdd) {
      item.textureId = resolveId(readInt(), true);
    }
    for (const item of toAdd) {
      const title: number = readInt();
      if (title < -1 || title >= titles.length) {
        throw new Error('Invalid binary Frustum response: title index.');
      }
      item.nodeTitle = title === -1 ? undefined : titles[title];
    }
    readDictionaryMatrices(toAdd);
    frustumData.push({ type, toDelete, toAdd });
  }
  if (offset !== bytes.length) {
    throw new Error('Invalid binary Frustum response: trailing data.');
  }
  return { frustumData };
}

/** Frustum v4 and compact JSON responses as a format plugin; prefers the binary layout in `Accept`. */
export class HsFrustumFormat implements HsFormatDecoder<HsFrustumView>, HsViewerPlugin {
  public readonly id: string = 'hyperscene-frustum';
  public readonly name: string = 'formats/frustum';
  public readonly contentTypes: readonly string[] = [HS_FRUSTUM_V4_CONTENT_TYPE, HS_FRUSTUM_JSON_CONTENT_TYPE];

  public install(context: HsViewerPluginContext): HsDisposable {
    return context.formats.register(this);
  }

  public decode(buffer: ArrayBuffer, context: HsFormatDecodeContext): HsFrustumView {
    if (context.contentType === HS_FRUSTUM_V4_CONTENT_TYPE) {
      return hsDecodeBinaryFrustum(buffer);
    }
    return hsDecodeJsonFrustum(JSON.parse(new TextDecoder().decode(buffer)) as HsCompactFrustumView);
  }
}
